// Every string the editor shows for the 2E screens and toasts: the editor's rows of the plan's copy table, verbatim and keyed as the table keys
// them. Developer `message` text, codes and `trace` never reach a user. The rows the table marks "kept" with no key (the Window.js limit and
// ascending-order toasts, Toasts.js's connection notice) stay in their components.
import {ApiError, NoResponseError} from "repository/errors";

export const COPY = {
    // The screens of the project-open gate (Task 4).
    screens: {
        processing: {
            title: "Processing your file",
            body: "Speedweaver is still detecting this file. The editor will open as soon as it's ready.",
            takingLonger: "This is taking longer than expected. You can retry now, or come back in a few minutes.",
        },
        notSupported: {
            title: "Not Supported",
            // The fallback. The screen shows a customerMessage only when it is one of NOT_SUPPORTED_CUSTOMER_MESSAGES, and otherwise this body.
            body: "We were unable to process this file. Check the Supported ECU List in your dashboard, or open a support ticket.",
        },
        processingError: {
            title: "Processing Error",
            body: "Something went wrong while processing this project. Our engineers have been notified; you can also report it below.",
        },
        beingPrepared: {
            title: "Preparing your project",
            body: "Your project is being moved to the new editor. This usually takes under a minute.",
            takingLonger: "This is taking longer than expected. You can retry now, or come back in a few minutes.",
        },
        conversionFailed: {
            title: "This project couldn't be prepared",
            body: "This project could not be prepared for the new editor. We have been notified.",
            reupload: "Speedweaver couldn't match this file to a supported release. Re-upload the file to tune it.",
        },
        noMapPacks: {
            title: "No maps available",
            body: "No map packs are available for this project. Please open a support ticket.",
        },
    },
    buttons: {
        retry: "Retry",
        back: "Back to projects",
        report: "Report an issue",
    },
    ticketSent: "Thank you. Our engineers have been notified and will be in touch.",

    // Toasts. The keys that are API error codes are the codes themselves.
    project_locked: "The project is busy with another change. Please try again in a moment.",
    project_not_converted: "This project isn't ready for editing yet. Please try again in a moment.",
    project_conversion_failed: "This project couldn't be prepared for the editor. Please contact support.",
    invalid_values: "Some values were rejected by the server. The map has been restored to its last saved state.",
    invalid_target: "That axis can't be edited on this map. The map has been restored to its last saved state.",
    read_only_axis: "This axis is fixed and can't be edited.",
    unknown_map_pack: "That map pack isn't available for this project. Showing the full pack.",
    // map_not_found has no toast: the window closes quietly and its layout entry is dropped.
    mapUnreadable: "This map can't be read from the uploaded file, so it can't be opened.",
    undo_not_newest: "A newer change on another map must be undone first.",
    nothingToUndo: "Nothing to undo.",
    undoBatchConfirm: n => `This will undo a change that touched ${n} maps. Continue?`,
    notSaved: "Your changes could not be saved because the project is busy. Press Ok to try again, or Cancel to discard them.",
    notSavedBadge: "Not saved",
    pastedNotNumber: "Pasted values must be numbers.",
    axisAffected: n => `${n} maps other than this one were affected - check edited maps under 'My Maps'`,
    map_read_burst: s => `You're opening maps very quickly. Waiting ${s} seconds before trying again.`,
    map_read_daily_cap: "This account has reached today's map read limit. It resets at midnight UTC. If you need more, open a support ticket from your dashboard.",
    store_error: "Something went wrong on the server. Please try again, and contact support if it continues.",
    // noResponse is the toast of a save or an undo POST that got no answer, and says what the editor does about the map (mapActions.handleSaveFailure and
    // mapActions.handleUndoFailure). Any other request that got no answer is `network`, which promises nothing about a map.
    noResponse: "No response from the server. Check your connection; the editor will put the map right when it's back.",
    network: "No response from the server. Check your connection and try again.",
    generic: "Something went wrong. Please try again, and contact support if it continues.",
    versionBanner: "A new version of the editor is available. Reload to update.",
    reload: "Reload",
};

// The two texts the API may send as a rejected project's `customerMessage` (copy-table.md, `screens.notSupported.body`). The NotSupported screen
// shows a customerMessage verbatim only when it is one of these, and COPY.screens.notSupported.body otherwise. They are the API's words and not
// editor copy, so they are not keys of COPY.
export const NOT_SUPPORTED_CUSTOMER_MESSAGES = Object.freeze([
    "This ECU/firmware isn't in our dataset.",
    "Something went wrong processing this file. Try again, or open a support ticket.",
]);

// The text of the API error codes that have a toast. undo_entry_not_found is the nothing-to-undo answer. project_not_converted and
// project_conversion_failed have one row each, whatever their reason or finding (the open flow shows its screens and does not ask here).
// A Map, so that a code such as "constructor" finds nothing.
const TEXT_BY_CODE = new Map([
    ["project_locked", COPY.project_locked],
    ["project_not_converted", COPY.project_not_converted],
    ["project_conversion_failed", COPY.project_conversion_failed],
    ["undo_not_newest", COPY.undo_not_newest],
    ["undo_entry_not_found", COPY.nothingToUndo],
    ["invalid_values", COPY.invalid_values],
    ["invalid_target", COPY.invalid_target],
    ["read_only_axis", COPY.read_only_axis],
    ["unknown_map_pack", COPY.unknown_map_pack],
    ["store_error", COPY.store_error],
    ["map_read_daily_cap", COPY.map_read_daily_cap],
]);

/**
 * The text a user may see for a failed request. It is never the API's own `message`.
 * An ApiError is looked up by its code; a code with no row (map_not_found, which is quiet, a Hydra 403 or 404, a Symfony 404) and any
 * other error give the generic text. A request that got no response gives the network text: a save or an undo POST that got none has COPY.noResponse, which
 * handleSaveFailure and handleUndoFailure show by themselves. `context` is reserved: no row needs it yet.
 */
export const copyFor = (error, context = {}) => {
    if (error instanceof ApiError) {
        if (error.code === "map_read_burst") {
            // Without the seconds the text would read "Waiting null seconds".
            return Number.isInteger(error.retryAfter) ? COPY.map_read_burst(error.retryAfter) : COPY.generic;
        }
        return TEXT_BY_CODE.get(error.code) ?? COPY.generic;
    }
    return error instanceof NoResponseError ? COPY.network : COPY.generic;
};
